import { useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X, ChevronLeft, ChevronRight } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { toBase, toDisplay, activeCurrency } from "../lib/format";
import { useFetch } from "../lib/api";
import { Seo, Loading, Empty, ErrorState } from "../components/Common";
import { ProductGrid, Swatch } from "../components/ProductCard";
import { Sheet, SheetContent, SheetTitle } from "../components/ui/sheet";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const SORTS = ["recommended", "bestsellers", "newest", "price_asc", "price_desc", "rating", "discount"];
const LIST_FACETS = [["color", "f_color", "c"], ["material", "f_material", "m"], ["brand", "f_brand", null], ["style", "f_style", "style"], ["size", "f_size", "s"],
  ["frame", "f_frame", "m"], ["upholstery", "f_upholstery", "m"], ["door", "f_door", "d"], ["drawers", "f_drawers", null], ["seats", "f_seats", null], ["finish", "f_finish", "fi"]];
const RANGES = [["price", "f_price"], ["width", "f_width"], ["height", "f_height"], ["depth", "f_depth"], ["weight", "f_weight"]];

const RangeFilter = ({ k, label, range, params, setParam }) => {
  const { t } = useI18n();
  const div = k === "price" ? 100 : 1;
  const lo = range?.[`${k}_min`], hi = range?.[`${k}_max`];
  const [a, setA] = useState(params.get(`${k}_min`) || "");
  const [b, setB] = useState(params.get(`${k}_max`) || "");
  if (lo == null || hi == null || lo === hi) return null;
  return (
    <AccordionItem value={k}>
      <AccordionTrigger className="text-sm font-semibold">{t(label)}</AccordionTrigger>
      <AccordionContent>
        <form className="flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); setParam({ [`${k}_min`]: a, [`${k}_max`]: b }); }}>
          <input className="field h-9" inputMode="numeric" placeholder={`${t("min")} ${k === "price" ? Math.floor(toDisplay(lo) / 100) : Math.floor(lo / div)}`} value={a} onChange={(e) => setA(e.target.value.replace(/[^\d]/g, ""))} aria-label={`${t(label)} ${t("min")}`} data-testid={`filter-${k}-min`} />
          <input className="field h-9" inputMode="numeric" placeholder={`${t("max")} ${k === "price" ? Math.ceil(toDisplay(hi) / 100) : Math.ceil(hi / div)}`} value={b} onChange={(e) => setB(e.target.value.replace(/[^\d]/g, ""))} aria-label={`${t(label)} ${t("max")}`} data-testid={`filter-${k}-max`} />
          <button className="btn-dark h-9 px-3" data-testid={`filter-${k}-apply`}>{t("apply")}</button>
        </form>
      </AccordionContent>
    </AccordionItem>
  );
};

const Filters = ({ facets, params, toggle, setParam, styleNames }) => {
  const { t, val } = useI18n();
  const lab = (pref, v) => (pref === "style" ? styleNames[v] || v : pref ? val(pref, v) : String(v));
  const flag = (k, v, label) => (
    <label className="flex items-center gap-2 text-sm py-1 cursor-pointer"><input type="checkbox" checked={params.get(k) === v} onChange={(e) => setParam({ [k]: e.target.checked ? v : "" })} data-testid={`filter-${k}-${v}`} />{label}</label>
  );
  return (
    <Accordion type="multiple" defaultValue={["price", "color", "material", "availability", "size"]} data-testid="filter-sidebar">
      {RANGES.slice(0, 1).map(([k, l]) => <RangeFilter key={k} k={k} label={l} range={facets.ranges} params={params} setParam={setParam} />)}
      <AccordionItem value="availability">
        <AccordionTrigger className="text-sm font-semibold">{t("f_availability")}</AccordionTrigger>
        <AccordionContent>
          {flag("in_stock", "1", t("f_in_stock"))}{flag("discount", "1", t("f_discount"))}{flag("storage", "1", t("f_storage"))}
          {flag("delivery_max", "7", t("f_delivery_7"))}{flag("delivery_max", "14", t("f_delivery_14"))}
          {flag("assembly", "0", t("f_assembly_no"))}{flag("assembly", "1", t("f_assembly_yes"))}{flag("rating_min", "4", "★ 4+")}
        </AccordionContent>
      </AccordionItem>
      {LIST_FACETS.map(([k, label, pref]) => facets[k]?.length > 1 && (
        <AccordionItem key={k} value={k}>
          <AccordionTrigger className="text-sm font-semibold">{t(label)}</AccordionTrigger>
          <AccordionContent className="space-y-1">
            {facets[k].map((o) => {
              const v = String(o.value);
              const on = (params.get(k) || "").split(",").includes(v);
              return (
                <label key={v} className="flex items-center gap-2 text-sm py-0.5 cursor-pointer" data-testid={`filter-category-${k}-${v}`}>
                  <input type="checkbox" checked={on} onChange={() => toggle(k, v)} />{k === "color" && <Swatch color={v} />}<span className="flex-1">{lab(pref, v)}</span><span className="text-xs text-[color:var(--hw-muted)]">{o.count}</span>
                </label>
              );
            })}
          </AccordionContent>
        </AccordionItem>
      ))}
      {RANGES.slice(1).map(([k, l]) => <RangeFilter key={k} k={k} label={l} range={facets.ranges} params={params} setParam={setParam} />)}
    </Accordion>
  );
};

const MODE_PARAM = { category: "category", room: "room", style: "style" };
const MODE_FLAG = { sale: "sale", new: "new", bestsellers: "bestsellers" };

function buildApiQuery(params, mode, slug, lang) {
  const qs = new URLSearchParams(params);
  qs.set("lang", lang);
  ["price_min", "price_max"].forEach((k) => qs.get(k) && qs.set(k, toBase(qs.get(k))));
  if (MODE_PARAM[mode]) qs.set(MODE_PARAM[mode], slug);
  if (MODE_FLAG[mode]) qs.set(MODE_FLAG[mode], "true");
  return qs.toString();
}

// URL-backed filter state: every change keeps unrelated params and resets pagination.
function useProductFilters(params, setParams, styleNames) {
  const { t, val } = useI18n();
  const setParam = (obj) => {
    const n = new URLSearchParams(params);
    Object.entries(obj).forEach(([k, v]) => (v === "" || v == null ? n.delete(k) : n.set(k, v)));
    n.delete("page");
    setParams(n);
  };
  const toggle = (k, v) => {
    const cur = (params.get(k) || "").split(",").filter(Boolean);
    setParam({ [k]: (cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v]).join(",") });
  };
  const chipLabel = (k, v) => {
    const flags = { in_stock: "f_in_stock", discount: "f_discount", storage: "f_storage", delivery_max: v === "7" ? "f_delivery_7" : "f_delivery_14", assembly: v === "1" ? "f_assembly_yes" : "f_assembly_no" };
    if (flags[k]) return t(flags[k]);
    if (k === "rating_min") return `★ ${v}+`;
    const m = k.match(/^(price|width|height|depth|weight)_(min|max)$/);
    if (m) return `${t(`f_${m[1]}`)} ${m[2] === "min" ? "≥" : "≤"} ${m[1] === "price" ? `${activeCurrency().code} ${v}` : v}`;
    return `${k}: ${v}`;
  };
  const chips = useMemo(() => {
    const out = [];
    params.forEach((v, k) => {
      if (["q", "sort", "page", "lang"].includes(k)) return;
      const f = LIST_FACETS.find((x) => x[0] === k);
      if (f) v.split(",").forEach((x) => out.push({ k, v: x, label: f[2] === "style" ? styleNames[x] || x : f[2] ? val(f[2], x) : x }));
      else out.push({ k, v, label: chipLabel(k, v) });
    });
    return out;
  }, [params, styleNames, val, t]); // eslint-disable-line react-hooks/exhaustive-deps
  const removeChip = (c) => (LIST_FACETS.find((x) => x[0] === c.k) ? toggle(c.k, c.v) : setParam({ [c.k]: "" }));
  const q = params.get("q");
  const clearAll = () => setParams(q ? { q } : {});
  const page = Number(params.get("page") || 1);
  const goPage = (p) => { const n = new URLSearchParams(params); n.set("page", p); setParams(n); window.scrollTo(0, 0); };
  return { setParam, toggle, chips, removeChip, clearAll, page, goPage };
}

export default function Listing({ mode }) {
  const { slug } = useParams();
  const { t, lang } = useI18n();
  const [params, setParams] = useSearchParams();
  const [drawer, setDrawer] = useState(false);
  const { data: cats } = useFetch(`/categories?lang=${lang}`, { cacheable: true });
  const { data: cols } = useFetch(`/collections?lang=${lang}`, { cacheable: true });
  const { currency } = useI18n();
  const apiQuery = useMemo(() => buildApiQuery(params, mode, slug, lang), [params, mode, slug, lang, currency]); // eslint-disable-line react-hooks/exhaustive-deps
  const { data, loading, error, reload } = useFetch(`/products?${apiQuery}`);
  const styleNames = useMemo(() => Object.fromEntries((cols || []).filter((c) => c.kind === "style").map((c) => [c.slug, c.name])), [cols]);

  const cat = cats?.find((c) => c.slug === slug);
  const col = cols?.find((c) => c.slug === slug);
  const q = params.get("q") || "";
  const title = { all: t("all_products"), category: cat?.name, room: col?.name, style: col?.name, search: t("search_results", { q }), sale: t("nav_sale"), new: t("nav_new"), bestsellers: t("nav_best") }[mode] || "";
  const crumbs = [];
  if (cat) { let c = cat; while (c) { crumbs.unshift(c); const p = c.parent; c = cats.find((x) => x.slug === p); } }
  const subs = mode === "category" ? (cats || []).filter((c) => c.parent === slug && c.count > 0) : [];
  const siblings = cat?.parent ? (cats || []).filter((c) => c.parent === cat.parent && c.slug !== slug) : [];

  const { setParam, toggle, chips, removeChip, clearAll, page, goPage } = useProductFilters(params, setParams, styleNames);
  const hasFilters = chips.length > 0;

  return (
    <div className="container-hw py-8" data-testid="listing-page">
      <Seo title={cat?.seo_title || title} description={cat?.description || `${title} – ANEL Furniture Schweiz`} noindex={hasFilters || mode === "search"} />
      <nav aria-label="Breadcrumb" className="text-xs text-[color:var(--hw-muted)] mb-6" data-testid="breadcrumbs">
        <ol className="flex flex-wrap gap-1.5"><li><Link to="/" className="hover:underline">{t("home")}</Link></li>
          {crumbs.map((c) => <li key={c.slug} className="flex gap-1.5"><span>/</span>{c.slug === slug ? <span aria-current="page" className="text-[color:var(--hw-ink)]">{c.name}</span> : <Link to={`/c/${c.slug}`} className="hover:underline">{c.name}</Link>}</li>)}
          {!cat && <li className="flex gap-1.5"><span>/</span><span className="text-[color:var(--hw-ink)]">{title}</span></li>}
        </ol>
      </nav>
      {(mode === "room" || mode === "style") && col && (
        <div className="relative h-56 sm:h-72 mb-8 overflow-hidden" data-testid="listing-banner"><img src={col.image} alt={col.name} className="w-full h-full object-cover" /><div className="absolute inset-0 bg-black/25" /></div>
      )}
      <header className="mb-8 max-w-3xl">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium mb-3" data-testid="listing-title">{title}</h1>
        {cat?.description && <p className="text-[color:var(--hw-muted)]" data-testid="listing-description">{cat.description}</p>}
      </header>
      {subs.length > 0 && (
        <div className="flex gap-2 overflow-x-auto no-scrollbar mb-8" data-testid="subcategory-nav">
          {subs.map((s) => <Link key={s.slug} to={`/c/${s.slug}`} className="shrink-0 px-4 py-2 text-sm border border-[color:var(--hw-line)] bg-white hover:border-[color:var(--hw-ink)]">{s.name} <span className="text-[color:var(--hw-muted)]">({s.count})</span></Link>)}
        </div>
      )}
      <div className="flex items-center justify-between gap-4 py-3 border-y border-[color:var(--hw-line)] mb-6 sticky top-[112px] lg:top-[116px] bg-[color:var(--hw-bg)] z-20">
        <button className="lg:hidden btn-outline h-9 px-4" onClick={() => setDrawer(true)} data-testid="mobile-filter-button"><SlidersHorizontal size={15} />{t("filters")}</button>
        <p className="text-sm" data-testid="listing-product-count">{data ? `${data.total} ${t("results")}` : ""}</p>
        <label className="flex items-center gap-2 text-sm"><span className="hidden sm:inline text-[color:var(--hw-muted)]">{t("sort")}</span>
          <select className="field h-9 w-auto" value={params.get("sort") || "recommended"} onChange={(e) => setParam({ sort: e.target.value })} data-testid="sort-select">
            {SORTS.map((s) => <option key={s} value={s}>{t(`sort_${s}`)}</option>)}
          </select>
        </label>
      </div>
      {hasFilters && (
        <div className="flex flex-wrap gap-2 mb-6" data-testid="active-filter-chips">
          {chips.map((c) => <button key={c.k + c.v} onClick={() => removeChip(c)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-[color:var(--hw-beige)] hover:bg-[color:var(--hw-line)]" data-testid={`chip-${c.k}-${c.v}`}>{c.label}<X size={12} /></button>)}
          <button onClick={clearAll} className="text-xs font-semibold link-u px-2" data-testid="clear-all-filters">{t("clear_all")}</button>
        </div>
      )}
      <div className="grid lg:grid-cols-[260px_1fr] gap-10">
        <aside className="hidden lg:block">{data && <Filters facets={data.facets} params={params} toggle={toggle} setParam={setParam} styleNames={styleNames} />}</aside>
        <div>
          {loading && !data ? <Loading /> : error ? <ErrorState onRetry={reload} /> : data.items.length === 0 ? (
            <Empty title={t("no_results")} text={t("no_results_d")} action={<div className="flex gap-3 justify-center">{hasFilters && <button className="btn-outline" onClick={clearAll}>{t("clear_all")}</button>}<Link to="/bestsellers" className="btn-primary">{t("best_sellers")}</Link></div>} />
          ) : (
            <div className={loading ? "opacity-60 transition-opacity" : ""}>
              <ProductGrid items={data.items} cols="lg:grid-cols-3 xl:grid-cols-3" />
              {data.pages > 1 && (
                <nav className="flex items-center justify-center gap-2 mt-14" aria-label="Pagination" data-testid="pagination">
                  <button className="btn-ghost" disabled={page <= 1} onClick={() => goPage(page - 1)} aria-label={t("prev")}><ChevronLeft size={16} /></button>
                  {Array.from({ length: data.pages }, (_, i) => i + 1).map((p) => <button key={p} onClick={() => goPage(p)} aria-current={p === page ? "page" : undefined} className={`w-9 h-9 text-sm ${p === page ? "bg-[color:var(--hw-ink)] text-white" : "hover:bg-[color:var(--hw-beige)]"}`} data-testid={`page-${p}`}>{p}</button>)}
                  <button className="btn-ghost" disabled={page >= data.pages} onClick={() => goPage(page + 1)} aria-label={t("next")}><ChevronRight size={16} /></button>
                </nav>
              )}
            </div>
          )}
          {siblings.length > 0 && (
            <div className="mt-16 pt-8 border-t border-[color:var(--hw-line)]" data-testid="related-categories">
              <p className="eyebrow mb-3">{t("categories")}</p>
              <div className="flex flex-wrap gap-x-6 gap-y-2">{siblings.map((s) => <Link key={s.slug} to={`/c/${s.slug}`} className="text-sm link-u">{s.name}</Link>)}</div>
            </div>
          )}
        </div>
      </div>
      <Sheet open={drawer} onOpenChange={setDrawer}>
        <SheetContent side="left" className="w-[90vw] max-w-sm overflow-y-auto" data-testid="mobile-filter-drawer">
          <SheetTitle className="font-serif text-2xl mb-4">{t("filters")}</SheetTitle>
          {data && <Filters facets={data.facets} params={params} toggle={toggle} setParam={setParam} styleNames={styleNames} />}
          <button className="btn-primary w-full mt-6" onClick={() => setDrawer(false)} data-testid="mobile-filter-apply">{t("show_results")} ({data?.total ?? 0})</button>
        </SheetContent>
      </Sheet>
    </div>
  );
}
